import { useState, useEffect } from 'react'
import { fetchWithAuth } from '../utils/api'
import { User } from '../types'

interface CompletedTask {
  id: number
  title: string
  description: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  status: 'completed' | 'verified'
  created_by_name: string
  assigned_to_name: string | null
  site_name: string | null
  area_name: string | null
  estimated_hours: number | null
  actual_hours: number | null
  due_date: string | null
  completed_at: string
  verified_at: string | null
  verified_by_name: string | null
  created_at: string
}

interface TaskHistoryProps {
  user: User
  onClose: () => void
  onTaskSelect: (taskId: number) => void
}

function TaskHistory({ onClose, onTaskSelect }: TaskHistoryProps) {
  const [tasks, setTasks] = useState<CompletedTask[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [filterPeriod, setFilterPeriod] = useState('all')
  const [sortBy, setSortBy] = useState('completed_at')

  useEffect(() => {
    fetchCompletedTasks()
  }, [page, filterPeriod, sortBy])

  const fetchCompletedTasks = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      params.append('status', 'completed,verified')
      params.append('page', page.toString())
      params.append('limit', '20')
      if (filterPeriod !== 'all') {
        const days = filterPeriod === 'week' ? 7 : filterPeriod === 'month' ? 30 : 90
        const cutoffDate = new Date()
        cutoffDate.setDate(cutoffDate.getDate() - days)
        params.append('completed_after', cutoffDate.toISOString())
      }
      params.append('sort_by', sortBy)
      params.append('sort_order', 'desc')

      const response = await fetchWithAuth(`/api/tasks?${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch completed tasks')
      }
      const data = await response.json()
      
      if (page === 1) {
        setTasks(data.tasks)
      } else {
        setTasks(prev => [...prev, ...data.tasks])
      }
      
      setHasMore(data.tasks.length === 20)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task history')
    } finally {
      setLoading(false)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#e74c3c'
      case 'high': return '#f39c12'
      case 'medium': return '#3498db'
      case 'low': return '#95a5a6'
      default: return '#95a5a6'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#27ae60'
      case 'verified': return '#2ecc71'
      default: return '#27ae60'
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toISOString().slice(0, 19).replace('T', ' ')
  }

  const formatDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffInMs = end.getTime() - start.getTime()
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    const diffInHours = Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (diffInDays > 0) {
      return `${diffInDays}d ${diffInHours}h`
    }
    return `${diffInHours}h`
  }

  const resetAndReload = () => {
    setPage(1)
    setTasks([])
  }

  const handleFilterChange = (newFilter: string) => {
    setFilterPeriod(newFilter)
    resetAndReload()
  }

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort)
    resetAndReload()
  }

  const loadMore = () => {
    setPage(prev => prev + 1)
  }

  return (
    <div className="task-history-overlay">
      <div className="task-history-container">
        <div className="task-history-header">
          <h3>📋 Task History</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="history-controls">
          <div className="filter-group">
            <label>Time Period:</label>
            <select value={filterPeriod} onChange={(e) => handleFilterChange(e.target.value)}>
              <option value="all">All Time</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="quarter">Last 3 Months</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sort By:</label>
            <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)}>
              <option value="completed_at">Completion Date</option>
              <option value="created_at">Created Date</option>
              <option value="priority">Priority</option>
              <option value="title">Title</option>
            </select>
          </div>
        </div>

        <div className="history-stats">
          <div className="stat-summary">
            <span className="stat-count">{tasks.length}</span>
            <span className="stat-label">Completed Tasks</span>
          </div>
        </div>

        <div className="history-content">
          {loading && page === 1 ? (
            <div className="loading">Loading task history...</div>
          ) : (
            <>
              {tasks.length === 0 ? (
                <div className="no-history">
                  <p>No completed tasks found</p>
                  <p>Tasks will appear here once they are marked as completed or verified</p>
                </div>
              ) : (
                <div className="history-list">
                  {tasks.map((task) => (
                    <div key={task.id} className="history-item">
                      <div className="history-item-header">
                        <h4 className="task-title">{task.title}</h4>
                        <div className="task-badges">
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(task.status) }}
                          >
                            {task.status === 'verified' ? '✓ Verified' : '✓ Completed'}
                          </span>
                          <span 
                            className="priority-badge"
                            style={{ backgroundColor: getPriorityColor(task.priority) }}
                          >
                            {task.priority}
                          </span>
                        </div>
                      </div>

                      {task.description && (
                        <p className="task-description">{task.description}</p>
                      )}

                      <div className="task-meta-grid">
                        <div className="meta-item">
                          <span className="meta-label">Created by:</span>
                          <span>{task.created_by_name}</span>
                        </div>
                        
                        {task.assigned_to_name && (
                          <div className="meta-item">
                            <span className="meta-label">Assigned to:</span>
                            <span>{task.assigned_to_name}</span>
                          </div>
                        )}
                        
                        {task.site_name && (
                          <div className="meta-item">
                            <span className="meta-label">Location:</span>
                            <span>
                              🏠 {task.site_name}
                              {task.area_name && ` → 📍 ${task.area_name}`}
                            </span>
                          </div>
                        )}
                        
                        <div className="meta-item">
                          <span className="meta-label">Completed:</span>
                          <span>{formatDate(task.completed_at)}</span>
                        </div>
                        
                        {task.status === 'verified' && task.verified_at && (
                          <div className="meta-item">
                            <span className="meta-label">Verified:</span>
                            <span>{formatDate(task.verified_at)} by {task.verified_by_name}</span>
                          </div>
                        )}
                        
                        <div className="meta-item">
                          <span className="meta-label">Duration:</span>
                          <span>{formatDuration(task.created_at, task.completed_at)}</span>
                        </div>
                        
                        {task.estimated_hours && (
                          <div className="meta-item">
                            <span className="meta-label">Est. Time:</span>
                            <span>{task.estimated_hours}h</span>
                          </div>
                        )}
                        
                        {task.actual_hours && (
                          <div className="meta-item">
                            <span className="meta-label">Actual Time:</span>
                            <span>{task.actual_hours}h</span>
                          </div>
                        )}
                      </div>

                      <div className="history-item-actions">
                        <button 
                          className="btn btn-sm"
                          onClick={() => onTaskSelect(task.id)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {hasMore && tasks.length > 0 && (
                <div className="load-more-container">
                  <button 
                    className="btn" 
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default TaskHistory